import { DGE_AUTH_METHOD, PLATFORM } from '@/constants/platform'
import { useAuthStore } from '@/stores/useAuthStore'
import { DDESession, DGESession } from '@/types/authentication'
import { DGEAuthMethod, Platform } from '@/types/platform'

import { DDE_ENDPOINTS } from './constants/ddeEndpoints'
import { DGE_ENDPOINTS } from './constants/dgeEndpoints'
import { KEYCLOAK_LOGIN_IN_PROGRESS_KEY } from './keycloak/KeycloakStateManager'
import { keycloakService } from './keycloak'
import { fetchData, postData, putData } from './request'

const DGE_ALLOWED_ROLES = ['ADMIN', 'SUPER_ADMIN']

const ddeLogin = async (userId: string, password: string) => {
  const res = await postData<{ userId: string; password: string }, DDESession>({
    endpointData: DDE_ENDPOINTS.Auth,
    data: { userId, password }
  })

  if (!res) {
    throw new Error('Login failed. Please check your credentials and try again.')
  }

  const user = {
    name: 'Not found',
    email: userId
  }

  useAuthStore.getState().setAuthData(res.token, res.userRole.roleName, PLATFORM.DDE, user)
}

const dgeLogin = async (username: string, password: string) => {
  const res = await postData<{ username: string; password: string }, DGESession>({
    endpointData: DGE_ENDPOINTS.Auth,
    data: { username, password }
  })

  if (!res) {
    throw new Error('Login failed. Please check your credentials and try again.')
  }

  if (!DGE_ALLOWED_ROLES.includes(res.account.role)) {
    throw new Error('Access denied. You do not have the required administrator role.')
  }

  const user = {
    name: res.account.fullName,
    email: res.account.email
  }

  useAuthStore
    .getState()
    .setAuthData(res.token, res.account.role, PLATFORM.DGE, user, DGE_AUTH_METHOD.LEGACY)
}

export const initializeKeycloakIfNeeded = async (): Promise<void> => {
  await keycloakService.initializeOnAppLoad()
}

export const login = (
  username: string,
  password: string,
  platform: Platform,
  authMethod?: DGEAuthMethod
) => {
  switch (platform) {
    case PLATFORM.DDE:
      return ddeLogin(username, password)
    case PLATFORM.DGE:
      if (authMethod === DGE_AUTH_METHOD.KEYCLOAK) {
        useAuthStore.getState().setPlatform(PLATFORM.DGE)
        localStorage.setItem(KEYCLOAK_LOGIN_IN_PROGRESS_KEY, 'true')

        return keycloakService.login()
      }
      return dgeLogin(username, password)
    default:
      return Promise.reject(new Error('Platform not supported'))
  }
}

export const logout = async (): Promise<void> => {
  const authState = useAuthStore.getState()

  if (authState.dgeAuthMethod === DGE_AUTH_METHOD.KEYCLOAK && keycloakService.isActive()) {
    await keycloakService.logout()
    return
  }

  useAuthStore.getState().clearAuthData()
}

export const forgotPassword = (email: string): Promise<unknown> | undefined => {
  const platform = useAuthStore.getState().platform

  if (platform === PLATFORM.DDE) {
    return putData({ endpointData: DDE_ENDPOINTS.ForgotPassword(email) })
  }

  if (platform === PLATFORM.DGE) {
    return fetchData({
      endpointData: DGE_ENDPOINTS.ForgotPassword(email),
      options: { headers: { 'Exchange-Auth': undefined } as unknown as HeadersInit }
    })
  }

  return undefined
}
